import { ScopedElementsMixin } from '@open-wc/scoped-elements';
import { html, LitElement } from 'lit-element';
import AabIcon from '@aab/sc-aab-icon/src/AabIcon';
import {
  sy_others_info as othersInfo, sy_others_info_solid as infoSolid,
  sy_shapes_close_large as closeLarge,
} from '@aab/sc-aab-icon-set/src/lib/systems';
import { createPopper } from '@popperjs/core';
import Styles from './styles.scss';

export default class AabInfoPopover extends ScopedElementsMixin(LitElement) {
  static get properties() {
    return {
      icon: {
        attribute: false,
        type: String,
      },
      open: {
        attribute: false,
        type: Boolean,
      },
      popper: {
        attribute: false,
        type: Object,
      },
    };
  }

  static get styles() {
    return [Styles];
  }

  static get scopedElements() {
    return {
      'aab-icon': AabIcon,
    };
  }

  constructor() {
    super();

    this.icon = othersInfo;
    this.open = false;
    this.popper = null;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', (event) => this.handleOutsideEvent(event));
    document.addEventListener('keydown', (event) => this.handleOutsideEvent(event));
  }

  disconnectedCallback() {
    document.removeEventListener('click', (event) => this.handleOutsideEvent(event));
    document.removeEventListener('keydown', (event) => this.handleOutsideEvent(event));
    super.disconnectedCallback();
  }

  updated() {
    if (this.open && this.popper) {
      this.popper.update();
    }
  }

  createPopper() {
    const popover = this.shadowRoot.querySelector('#popover');

    this.popper = createPopper(this, popover, {
      placement: 'top',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
        },
      ],
    });
  }

  handleIconHover(event) {
    if (this.open) {
      this.icon = infoSolid;
    } else if (!this.open && (event.type === 'mouseenter' || event.type === 'focusin')) {
      this.icon = infoSolid;
    } else {
      this.icon = othersInfo;
    }
  }

  togglePopover() {
    this.open = !this.open;

    if (this.open) {
      if (!this.popper) {
        this.createPopper();
      }
    } else {
      this.destroyPopper();
    }
  }

  destroyPopper() {
    this.popper.destroy();
    this.popper = null;
    this.icon = othersInfo;
  }

  handleOutsideEvent(event) {
    if (event.key === 'Escape' && this.open) {
      this.togglePopover();
      this.shadowRoot.querySelector('#info-button').focus();
      this.icon = infoSolid;
    } else if (this.open && !event.composedPath().filter((element) => element === this).length) {
      this.togglePopover();
    }
  }

  handleKeyDown(event) {
    if (
      event.key === 'Enter'
      || event.key === ' '
      || event.key === 'Spacebar'
    ) {
      event.preventDefault();
      this.togglePopover();
      this.shadowRoot.querySelector('#info-button').focus();
      this.icon = infoSolid;
    }
  }

  render() {
    return html`
      <aab-icon
        tabindex="0"
        size="2.5"
        role="button"
        aria-label="Open info-popover"
        aria-haspopup="true"
        id="info-button"
        svg="${this.icon}"
        @mouseenter="${this.handleIconHover}"
        @mouseleave="${this.handleIconHover}"
        @focusin="${this.handleIconHover}"
        @focusout="${this.handleIconHover}"
        @click="${this.togglePopover}"
        @keydown="${(event) => this.handleKeyDown(event)}"
      ></aab-icon>
      <div id="popover" role="dialog" aria-label="popover " class="${this.open ? 'd-flex' : 'd-none'} p-2 pb-0 flex-column border_box rounded-4">
        <div id="arrow" data-popper-arrow></div>
        <div class="h4 p">
          <aab-icon
            role="button"
            aria-label="Sluit info-popover"
            tabindex="0"
            id="close-button"
            size="3"
            svg="${closeLarge}"
            @click="${this.togglePopover}"
            @keydown="${(event) => this.handleKeyDown(event)}"
          ></aab-icon>
          <div tabindex="0">
            <slot></slot>
          </div>
        </div>
      </div>
    `;
  }
}
